import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { LoginButton } from '@components';
import { useAppCookies } from '@app/providers/AppCookieProvider';
import { clearReturnTo, readReturnTo, rememberReturnTo, safeReturnTo } from '@utils/returnTo';

interface HomeProps {
  children?: any;
}

export const Home: React.FC<HomeProps> = () => {
  const { auth, profile } = useAppCookies();
  const [searchParams] = useSearchParams();
  const requestedReturnTo = safeReturnTo(searchParams.get('returnTo'));

  // Matches what UserRoleProvider calls logged in, otherwise a missing profile cookie bounces between here and RoleGuard
  const isLoggedIn = auth.available && profile.available;

  // Held in state because the effect below clears storage while the redirect is still rendering
  const [returnTo] = useState(() => requestedReturnTo ?? readReturnTo());

  useEffect(() => {
    // Signing in leaves the app, so the destination has to outlive this page. Login lands on a URL without
    // returnTo, which uses it up; a logged-out visit to one starts over, so someone logging out doesn't get sent
    // back to the page they left on their next sign-in. A sign-in page left open keeps returnTo in its URL and
    // sees the login cookies before a magic link's new tab loads, so it leaves the destination for that tab.
    if (!requestedReturnTo) {
      clearReturnTo();
    } else if (!isLoggedIn) {
      rememberReturnTo(requestedReturnTo);
    }
  }, [isLoggedIn, requestedReturnTo]);

  return isLoggedIn ? (
    <Navigate to={returnTo ?? '/teams'} replace />
  ) : (
    <LoginButton loginButtonHelp="Sign in with Google" />
  );
};
